// InscriptionClient.jsx - Version améliorée avec validation des dates
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { APP_CONFIG } from '../../config/appConfig';
import {
  Users, GraduationCap, Plus, Trash2, Search,
  Calendar, CheckCircle, XCircle, Loader2,
  UserPlus, Building2, Clock, AlertCircle,
  Edit, Eye, RefreshCw, Play, Pause, X, Check,
  User, Mail, Phone, BookOpen, Calendar as CalendarIcon,
  DollarSign, Ban, Undo, RotateCcw,
  Link, CalendarClock, BarChart3, FileText
} from 'lucide-react';

export default function InscriptionClient() {
  const { token, user, isAuthenticated } = useAuth();
  const clientId = user?.clientId || user?.client?.id || user?.id;

  const [inscriptions, setInscriptions] = useState([]);
  const [students, setStudents] = useState([]);
  const [studentsMap, setStudentsMap] = useState({});
  const [formations, setFormations] = useState([]);
  const [formationsMap, setFormationsMap] = useState({});
  const [evaluations, setEvaluations] = useState([]);
  const [evaluationsMap, setEvaluationsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('TOUS');

  const [selectedInscriptions, setSelectedInscriptions] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignType, setAssignType] = useState('FORMATION');
  const [assignEntityId, setAssignEntityId] = useState('');

  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressInscription, setProgressInscription] = useState(null);
  const [newProgression, setNewProgression] = useState(0);

  const [showProlongationModal, setShowProlongationModal] = useState(false);
  const [prolongationInscription, setProlongationInscription] = useState(null);
  const [prolongationJours, setProlongationJours] = useState(7);
  const [prolongationRaison, setProlongationRaison] = useState('');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    etudiantId: '', formationId: '', evaluationId: '', typeInscription: 'FORMATION',
    dateInscription: new Date().toISOString().split('T')[0], statut: 'EN_ATTENTE'
  });

  // ⭐ NOUVEAU : état pour les limites de dates
  const [dateLimits, setDateLimits] = useState({ debut: null, fin: null });
  const [dateError, setDateError] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({ id: null, etudiantId: '', formationId: '', dateInscription: '', statut: '' });
  // ⭐ NOUVEAU : état pour les limites de dates en édition
  const [editDateLimits, setEditDateLimits] = useState({ debut: null, fin: null });
  const [editDateError, setEditDateError] = useState(null);
  const [editingLoading, setEditingLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!token || !clientId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Étudiants
      const studentsRes = await fetch(
        `${APP_CONFIG.BASE_URL}/api/v1/client/etudiants?page=0&size=100`,
        { headers: { "Authorization": `Bearer ${token}`, "Accept": "application/json" } }
      );
      let studentsData = [];
      if (studentsRes.ok) {
        const data = await studentsRes.json();
        studentsData = data.content || data.data || data || [];
        setStudents(studentsData);
        const map = {};
        studentsData.forEach(s => { map[s.id] = s; });
        setStudentsMap(map);
      }

      // Formations
      const formationsRes = await fetch(
        `${APP_CONFIG.BASE_URL}/api/v1/client/formations`,
        { headers: { "Authorization": `Bearer ${token}`, "Accept": "application/json" } }
      );
      let formationsData = [];
      if (formationsRes.ok) {
        const data = await formationsRes.json();
        formationsData = data.content || data.data || data || [];
        setFormations(formationsData);
        const map = {};
        formationsData.forEach(f => { map[f.id] = f; });
        setFormationsMap(map);
      }

      // Évaluations
      const evaluationsRes = await fetch(
        `${APP_CONFIG.BASE_URL}/api/v1/client/evaluations?page=0&size=100`,
        { headers: { "Authorization": `Bearer ${token}`, "Accept": "application/json" } }
      );
      let evaluationsData = [];
      if (evaluationsRes.ok) {
        const data = await evaluationsRes.json();
		// Extraction sécurisée du tableau
		if (data && typeof data === 'object') {
		    if (Array.isArray(data.content)) {
		      evaluationsData = data.content;
		    } else if (Array.isArray(data.data)) {
		      evaluationsData = data.data;
		    } else if (Array.isArray(data)) {
		      evaluationsData = data;
		    } else {
		      // Si aucun tableau n'est trouvé, on logue la structure pour déboguer
		      console.warn('Format de réponse inattendu pour /evaluations :', data);
		      evaluationsData = [];
		    }
		}
		setEvaluations(evaluationsData);
		const map = {};
		evaluationsData.forEach(e => { map[e.id] = e; });
		setEvaluationsMap(map);
      }

      // Inscriptions
      const inscriptionsRes = await fetch(
        `${APP_CONFIG.BASE_URL}/api/v1/client/inscriptions?page=0&size=100`,
        { headers: { "Authorization": `Bearer ${token}`, "Accept": "application/json" } }
      );
      if (inscriptionsRes.ok) {
        const data = await inscriptionsRes.json();
        const inscriptionsData = data.content || data.data || data || [];
        setInscriptions(inscriptionsData);
        setSelectedInscriptions([]);
      } else {
        setInscriptions([]);
      }
    } catch (err) {
      console.error("Erreur récupération données:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ⭐ NOUVEAU : mise à jour des dates limites dans le formulaire de création
  useEffect(() => {
    let debut = null;
    let fin = null;
    if (formData.typeInscription === 'FORMATION' && formData.formationId) {
      const formation = formationsMap[formData.formationId];
      if (formation) {
        debut = formation.dateDebutInscription;
        fin = formation.dateFinInscription;
      }
    } else if (formData.typeInscription === 'EVALUATION' && formData.evaluationId) {
      const evaluation = evaluationsMap[formData.evaluationId];
      if (evaluation) {
        debut = evaluation.dateDebutInscription;
        fin = evaluation.dateFinInscription;
      }
    }
    setDateLimits({ debut, fin });
    // Réinitialiser l'erreur de date si les limites changent
    setDateError(null);
    // Vérifier la date courante
    if (formData.dateInscription) {
      const currentDate = formData.dateInscription;
      if (debut && currentDate < debut) {
        setDateError(`La date ne peut pas être antérieure au ${debut}`);
      } else if (fin && currentDate > fin) {
        setDateError(`La date ne peut pas être postérieure au ${fin}`);
      } else {
        setDateError(null);
      }
    }
  }, [formData.typeInscription, formData.formationId, formData.evaluationId, formData.dateInscription, formationsMap, evaluationsMap]);

  // ⭐ NOUVEAU : mise à jour des dates limites dans le formulaire d'édition
  useEffect(() => {
    if (!editData.formationId) {
      setEditDateLimits({ debut: null, fin: null });
      setEditDateError(null);
      return;
    }
    const formation = formationsMap[editData.formationId];
    if (formation) {
      const debut = formation.dateDebutInscription;
      const fin = formation.dateFinInscription;
      setEditDateLimits({ debut, fin });
      // Vérifier la date courante
      if (editData.dateInscription) {
        const currentDate = editData.dateInscription;
        if (debut && currentDate < debut) {
          setEditDateError(`La date ne peut pas être antérieure au ${debut}`);
        } else if (fin && currentDate > fin) {
          setEditDateError(`La date ne peut pas être postérieure au ${fin}`);
        } else {
          setEditDateError(null);
        }
      }
    }
  }, [editData.formationId, editData.dateInscription, formationsMap]);

  // Transitions
  const getAvailableTransitions = (currentStatut) => {
    const transitions = {
      'EN_ATTENTE': [
        { value: 'VALIDEE', label: 'Valider', icon: '✅', action: 'valider' },
        { value: 'REJETEE', label: 'Rejeter', icon: '❌', action: 'rejeter' }
      ],
      'VALIDEE': [
        { value: 'INSCRIT', label: 'Paiement validé', icon: '💰', action: 'payer' },
        { value: 'ANNULEE', label: 'Annuler', icon: '🚫', action: 'annuler' }
      ],
      'INSCRIT': [
        { value: 'EN_COURS', label: 'Démarrer', icon: '▶️', action: 'demarrer' },
        { value: 'ANNULEE', label: 'Annuler', icon: '🚫', action: 'annuler' }
      ],
      'EN_COURS': [
        { value: 'TERMINE', label: 'Terminer', icon: '🏁', action: 'terminer' },
        { value: 'ABANDONNEE', label: 'Abandonner', icon: '⛔', action: 'abandonner' }
      ],
      'TERMINE': [], 'REJETEE': [], 'ANNULEE': [], 'ABANDONNEE': []
    };
    return transitions[currentStatut] || [];
  };

  const isStatusModifiable = (statut) => {
    return ['EN_ATTENTE', 'VALIDEE', 'INSCRIT', 'EN_COURS'].includes(statut);
  };

  const handleTransition = async (inscriptionId, newStatus) => {
    const inscription = inscriptions.find(i => i.id === inscriptionId);
    if (!inscription) return;
    let motif = null;
    if (newStatus === 'REJETEE' || newStatus === 'ABANDONNEE') {
      motif = prompt(`Motif du ${newStatus === 'REJETEE' ? 'rejet' : 'abandon'} :`);
      if (motif === null) return;
      if (!motif.trim()) { alert('Le motif est obligatoire.'); return; }
    }
    if (newStatus === 'ANNULEE' || newStatus === 'ABANDONNEE') {
      if (!window.confirm(`Êtes-vous sûr de vouloir ${newStatus === 'ANNULEE' ? 'annuler' : 'abandonner'} ?`)) return;
    }
    setLoading(true);
    setError(null);
    try {
      let endpoint = '';
      let method = 'PATCH';
      switch (newStatus) {
        case 'VALIDEE': endpoint = `/inscriptions/${inscriptionId}/valider`; break;
        case 'REJETEE': endpoint = `/inscriptions/${inscriptionId}/rejeter?motif=${encodeURIComponent(motif)}`; break;
        case 'INSCRIT': endpoint = `/inscriptions/${inscriptionId}/payer`; break;
        case 'ANNULEE': endpoint = `/inscriptions/${inscriptionId}/annuler-par-etudiant`; break;
        case 'EN_COURS': endpoint = `/inscriptions/${inscriptionId}/demarrer`; break;
        case 'TERMINE': endpoint = `/inscriptions/${inscriptionId}/progression?progression=100`; break;
        case 'ABANDONNEE': endpoint = `/inscriptions/${inscriptionId}/abandonner?motif=${encodeURIComponent(motif)}`; break;
        default: throw new Error('Action non supportée');
      }
      const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/client${endpoint}`, {
        method,
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
          "Accept": "application/json"
        }
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || data.message || 'Erreur');
      }
      await fetchData();
      alert('✅ Statut mis à jour');
    } catch (err) {
      console.error("Erreur transition:", err);
      setError(err.message);
      alert(`❌ ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Sélection multiple
  const toggleSelectAll = () => {
    if (selectedInscriptions.length === filtered.length) setSelectedInscriptions([]);
    else setSelectedInscriptions(filtered.map(ins => ins.id));
  };
  const toggleSelectOne = (id) => {
    setSelectedInscriptions(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  // Assignation en masse
  const handleAssign = async () => {
    if (!assignEntityId) { alert('Veuillez sélectionner une entité'); return; }
    if (selectedInscriptions.length === 0) { alert('Sélectionnez au moins une inscription'); return; }
    const etudiantIds = selectedInscriptions.map(id => inscriptions.find(i => i.id === id)?.etudiantId).filter(id => id);
    if (etudiantIds.length === 0) { alert('Aucun étudiant valide'); return; }
    const endpoint = assignType === 'FORMATION'
      ? `/formations/${assignEntityId}/assign`
      : `/evaluations/${assignEntityId}/assign`;
    setLoading(true);
    try {
      const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/client${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(etudiantIds)
      });
      if (!response.ok) throw new Error('Erreur assignation');
      alert('✅ Assignation réussie');
      setShowAssignModal(false);
      setSelectedInscriptions([]);
      fetchData();
    } catch (err) {
      alert('❌ ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Progression
  const handleUpdateProgression = async () => {
    if (newProgression < 0 || newProgression > 100) { alert('Progression entre 0 et 100'); return; }
    setLoading(true);
    try {
      const response = await fetch(
        `${APP_CONFIG.BASE_URL}/api/v1/client/inscriptions/${progressInscription.id}/progression?progression=${newProgression}`,
        { method: 'PATCH', headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Erreur mise à jour');
      alert('✅ Progression mise à jour');
      setShowProgressModal(false);
      setProgressInscription(null);
      fetchData();
    } catch (err) {
      alert('❌ ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Prolongation
  const handleApproveProlongation = async () => {
    if (!prolongationInscription || !prolongationInscription.sessionEtudiantId) {
      alert('Session étudiant non trouvée.');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(
        `${APP_CONFIG.BASE_URL}/api/v1/client/prolongations/${prolongationInscription.sessionEtudiantId}/approve`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ joursSupplementaires: prolongationJours })
        }
      );
      if (!response.ok) throw new Error('Erreur approbation');
      alert('✅ Prolongation approuvée');
      setShowProlongationModal(false);
      setProlongationInscription(null);
      fetchData();
    } catch (err) {
      alert('❌ ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ⭐ NOUVEAU : gestionnaire de changement de date pour la création
  const handleDateChange = (e) => {
    const selectedDate = e.target.value;
    const { debut, fin } = dateLimits;
    let errorMsg = null;
    if (debut && selectedDate < debut) {
      errorMsg = `La date ne peut pas être antérieure au ${debut}`;
    } else if (fin && selectedDate > fin) {
      errorMsg = `La date ne peut pas être postérieure au ${fin}`;
    }
    setDateError(errorMsg);
    setFormData(prev => ({ ...prev, dateInscription: selectedDate }));
  };

  // ⭐ NOUVEAU : gestionnaire de changement de date pour l'édition
  const handleEditDateChange = (e) => {
    const selectedDate = e.target.value;
    const { debut, fin } = editDateLimits;
    let errorMsg = null;
    if (debut && selectedDate < debut) {
      errorMsg = `La date ne peut pas être antérieure au ${debut}`;
    } else if (fin && selectedDate > fin) {
      errorMsg = `La date ne peut pas être postérieure au ${fin}`;
    }
    setEditDateError(errorMsg);
    setEditData(prev => ({ ...prev, dateInscription: selectedDate }));
  };

  // Création
  const handleCreate = async (e) => {
    e.preventDefault();
    // Vérification des dates avant envoi
    if (dateError) {
      alert(dateError);
      return;
    }
    setLoading(true);
    setError(null);
    if (formData.typeInscription === 'FORMATION' && !formData.formationId) {
      alert('Veuillez sélectionner une formation');
      setLoading(false);
      return;
    }
    if (formData.typeInscription === 'EVALUATION' && !formData.evaluationId) {
      alert('Veuillez sélectionner une évaluation');
      setLoading(false);
      return;
    }
    try {
      let response;
      if (formData.typeInscription === 'FORMATION') {
        response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/client/inscriptions`, {
          method: "POST",
          headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({
            etudiantId: parseInt(formData.etudiantId),
            formationId: parseInt(formData.formationId),
            dateInscription: formData.dateInscription,
            statut: formData.statut
          })
        });
      } else {
        response = await fetch(
          `${APP_CONFIG.BASE_URL}/api/v1/client/evaluations/${formData.evaluationId}/inscrire?etudiantId=${formData.etudiantId}&dateInscription=${formData.dateInscription}&statut=${formData.statut}`,
          { method: "POST", headers: { "Authorization": `Bearer ${token}`, "Accept": "application/json" } }
        );
      }
      if (response.ok) {
        setShowCreateModal(false);
        resetForm();
        await fetchData();
        alert("✅ Inscription créée");
      } else {
        const data = await response.json();
        alert(`❌ ${data.error || "Erreur"}`);
      }
    } catch (err) {
      console.error("Erreur inscription:", err);
      alert("❌ Erreur technique");
    } finally {
      setLoading(false);
    }
  };

  // Édition
  const openEditModal = (inscription) => {
    setEditData({
      id: inscription.id,
      etudiantId: inscription.etudiantId.toString(),
      formationId: inscription.formationId ? inscription.formationId.toString() : '',
      dateInscription: inscription.dateInscription ? inscription.dateInscription.split('T')[0] : '',
      statut: inscription.statut
    });
    setEditDateLimits({ debut: null, fin: null });
    setEditDateError(null);
    setShowEditModal(true);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (editDateError) {
      alert(editDateError);
      return;
    }
    setEditingLoading(true);
    setError(null);
    try {
      const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/client/inscriptions/${editData.id}`, {
        method: "PUT",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          etudiantId: parseInt(editData.etudiantId),
          formationId: parseInt(editData.formationId),
          dateInscription: editData.dateInscription,
          statut: editData.statut
        })
      });
      if (response.ok) {
        setShowEditModal(false);
        await fetchData();
        alert("✅ Inscription modifiée");
      } else {
        const data = await response.json();
        alert(`❌ ${data.error || "Erreur"}`);
      }
    } catch (err) {
      console.error("Erreur modification:", err);
      alert("❌ Erreur technique");
    } finally {
      setEditingLoading(false);
    }
  };

  // Suppression
  const handleDelete = async (inscriptionId) => {
    if (!window.confirm('Supprimer cette inscription ?')) return;
    setLoading(true);
    try {
      const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/client/inscriptions/${inscriptionId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}`, "Accept": "application/json" }
      });
      if (response.ok) {
        await fetchData();
        alert("✅ Inscription supprimée");
      } else {
        const data = await response.json();
        alert(`❌ ${data.error || "Erreur"}`);
      }
    } catch (err) {
      console.error("Erreur suppression:", err);
      alert("❌ Erreur technique");
    } finally {
      setLoading(false);
    }
  };

  // Formulaires
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Réinitialiser l'erreur de date si on change le type ou l'entité
    if (name === 'typeInscription' || name === 'formationId' || name === 'evaluationId') {
      setDateError(null);
    }
  };
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({ ...prev, [name]: value }));
    if (name === 'formationId') {
      setEditDateError(null);
    }
  };
  const resetForm = () => {
    setFormData({
      etudiantId: '', formationId: '', evaluationId: '', typeInscription: 'FORMATION',
      dateInscription: new Date().toISOString().split('T')[0], statut: 'EN_ATTENTE'
    });
    setDateLimits({ debut: null, fin: null });
    setDateError(null);
    setError(null);
  };

  // Rendu
  const getStatusBadge = (statut) => {
    const statusMap = {
      'EN_ATTENTE': { label: '⏳ En attente', class: 'bg-amber-100 text-amber-700' },
      'VALIDEE': { label: '✅ Validée', class: 'bg-blue-100 text-blue-700' },
      'INSCRIT': { label: '📝 Inscrit', class: 'bg-green-100 text-green-700' },
      'EN_COURS': { label: '▶️ En cours', class: 'bg-indigo-100 text-indigo-700' },
      'TERMINE': { label: '🏁 Terminé', class: 'bg-gray-100 text-gray-700' },
      'REJETEE': { label: '❌ Rejetée', class: 'bg-red-100 text-red-700' },
      'ANNULEE': { label: '🚫 Annulée', class: 'bg-red-100 text-red-700' },
      'ABANDONNEE': { label: '⛔ Abandonnée', class: 'bg-orange-100 text-orange-700' }
    };
    return statusMap[statut] || { label: statut || 'N/A', class: 'bg-gray-100 text-gray-700' };
  };

  const getStudentInfo = (id) => studentsMap[id] || null;
  const getFormationInfo = (id) => formationsMap[id] || null;
  const getEvaluationInfo = (id) => evaluationsMap[id] || null;

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const filtered = Array.isArray(inscriptions)
    ? inscriptions.filter(ins => {
        const student = getStudentInfo(ins.etudiantId);
        const formation = getFormationInfo(ins.formationId);
        const evaluation = getEvaluationInfo(ins.evaluationId);
        const studentName = student ? `${student.prenom} ${student.nom}`.toLowerCase() : '';
        const formationTitle = formation ? formation.titre.toLowerCase() : '';
        const evaluationTitle = evaluation ? evaluation.titre.toLowerCase() : '';
        const search = searchTerm.toLowerCase();
        const type = ins.typeInscription || (formation ? 'FORMATION' : evaluation ? 'EVALUATION' : 'INCONNU');
        if (filterType === 'FORMATION' && type !== 'FORMATION') return false;
        if (filterType === 'EVALUATION' && type !== 'EVALUATION') return false;
        return studentName.includes(search) || formationTitle.includes(search) || evaluationTitle.includes(search);
      })
    : [];

  if (!isAuthenticated || !token) {
    return (
      <div className="p-6 text-center">
        <div className="max-w-md mx-auto bg-amber-50 border border-amber-200 rounded-xl p-6">
          <AlertCircle size={24} className="mx-auto text-amber-500 mb-2" />
          <p className="text-gray-700 font-medium">Accès réservé aux clients</p>
          <p className="text-sm text-gray-500 mt-1">Veuillez vous connecter.</p>
        </div>
      </div>
    );
  }

  if (!clientId) {
    return (
      <div className="p-6 text-center">
        <div className="max-w-md mx-auto bg-red-50 border border-red-200 rounded-xl p-6">
          <AlertCircle size={24} className="mx-auto text-red-500 mb-2" />
          <p className="text-gray-700 font-medium">Erreur de configuration</p>
          <p className="text-sm text-gray-500 mt-1">Impossible de récupérer l'identifiant du client.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      {/* En-tête */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-gray-200 pb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Gestion des Inscriptions</h2>
          <p className="text-sm text-gray-500">Gérez les inscriptions et les statuts des étudiants</p>
          <div className="text-xs text-gray-400 mt-1">
            {students.length} étudiants · {formations.length} formations · {evaluations.length} évaluations · {inscriptions.length} inscriptions
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            style={{ maxWidth: '200px' }}
          />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            style={{ maxWidth: '150px' }}
          >
            <option value="TOUS">Tous</option>
            <option value="FORMATION">📘 Formations</option>
            <option value="EVALUATION">📝 Évaluations</option>
          </select>
          {selectedInscriptions.length > 0 && (
            <button onClick={() => setShowAssignModal(true)} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm flex items-center gap-1">
              <Link size={16} /> Assigner ({selectedInscriptions.length})
            </button>
          )}
          <button onClick={() => { resetForm(); setShowCreateModal(true); }} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm flex items-center gap-1">
            <Plus size={16} /> Nouvelle inscription
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>}

      {/* Tableau */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-center"><input type="checkbox" checked={selectedInscriptions.length === filtered.length && filtered.length > 0} onChange={toggleSelectAll} className="w-4 h-4" /></th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Étudiant</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Formation / Évaluation</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Période</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && inscriptions.length === 0 ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center"><Loader2 className="animate-spin mx-auto text-indigo-600" size={24} /><p className="text-xs text-gray-500 mt-2">Chargement...</p></td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center"><UserPlus size={40} className="mx-auto text-gray-300" /><p className="text-sm text-gray-400 mt-2">Aucune inscription</p></td></tr>
              ) : (
                filtered.map(ins => {
                  const student = getStudentInfo(ins.etudiantId);
                  const formation = getFormationInfo(ins.formationId);
                  const evaluation = getEvaluationInfo(ins.evaluationId);
                  const canModify = isStatusModifiable(ins.statut);
                  const transitions = getAvailableTransitions(ins.statut);
                  const isSelected = selectedInscriptions.includes(ins.id);
                  const typeInscription = ins.typeInscription || (formation ? 'FORMATION' : evaluation ? 'EVALUATION' : 'INCONNU');
                  const typeLabel = typeInscription === 'FORMATION' ? 'Formation' : typeInscription === 'EVALUATION' ? 'Évaluation' : 'Inconnu';
                  const badgeColor = typeInscription === 'FORMATION' ? 'bg-blue-50 text-blue-700 border-blue-200' : typeInscription === 'EVALUATION' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-gray-50 text-gray-700 border-gray-200';
                  const typeIcon = typeInscription === 'FORMATION' ? <BookOpen size={12} /> : <FileText size={12} />;
                  const titre = formation?.titre || evaluation?.titre || `ID: ${ins.formationId || ins.evaluationId}`;

                  return (
                    <tr key={ins.id}>
                      <td className="px-4 py-3 text-center"><input type="checkbox" checked={isSelected} onChange={() => toggleSelectOne(ins.id)} className="w-4 h-4" /></td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900">{student ? `${student.prenom} ${student.nom}` : `ID: ${ins.etudiantId}`}</div>
                        <div className="text-[10px] text-gray-400 flex items-center gap-1"><span className="font-mono">#{ins.codeEtudiant || ins.etudiantId}</span><span className="text-gray-300">·</span><span>ID: {ins.id}</span></div>
                      </td>
                      <td className="px-4 py-3">
                        {student ? (
                          <div className="space-y-0.5">
                            <div className="text-xs text-gray-600 flex items-center gap-1"><Mail size={12} className="text-gray-400" /> {student.email || '-'}</div>
                            <div className="text-xs text-gray-600 flex items-center gap-1"><Phone size={12} className="text-gray-400" /> {student.mobile || '-'}</div>
                          </div>
                        ) : <span className="text-xs text-gray-400">Étudiant non trouvé</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          <div className="font-serif text-gray-700">{titre}</div>
                          <span className={`text-xs px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${badgeColor}`}>
                            {typeIcon} {typeLabel}
                          </span>
                          {(formation?.code || evaluation?.code) && <div className="text-[10px] text-gray-400 font-mono">Code: {formation?.code || evaluation?.code}</div>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs space-y-0.5">
                          <div className="flex items-center gap-1"><CalendarIcon size={12} className="text-gray-400" /> Début: {formatDate(ins.dateDebut || ins.dateInscription)}</div>
                          <div className="flex items-center gap-1"><CalendarIcon size={12} className="text-gray-400" /> Fin: {formatDate(ins.dateFin)}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold inline-block ${getStatusBadge(ins.statut).class}`}>{getStatusBadge(ins.statut).label}</span>
                          {ins.progression !== undefined && ins.progression > 0 && (
                            <div className="w-full max-w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${Math.min(ins.progression, 100)}%` }} />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1 flex-wrap">
                          {canModify && transitions.map(transition => (
                            <button key={transition.value} onClick={() => handleTransition(ins.id, transition.value)} className="px-2 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-xs disabled:opacity-50" disabled={loading}>
                              {transition.icon} {transition.label}
                            </button>
                          ))}
                          {ins.statut === 'EN_COURS' && (
                            <button onClick={() => { setProgressInscription(ins); setNewProgression(ins.progression || 0); setShowProgressModal(true); }} className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-green-50 rounded-lg transition" title="Modifier progression">
                              <BarChart3 size={14} />
                            </button>
                          )}
                          {ins.statut === 'EN_COURS' && (
                            <button onClick={() => { setProlongationInscription(ins); setShowProlongationModal(true); }} className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-green-50 rounded-lg transition" title="Approuver prolongation">
                              <CalendarClock size={14} />
                            </button>
                          )}
                          <button onClick={() => openEditModal(ins)} className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-green-50 rounded-lg transition" title="Modifier"><Edit size={16} /></button>
                          {(ins.statut === 'EN_ATTENTE' || ins.statut === 'REJETEE' || ins.statut === 'ANNULEE') && (
                            <button onClick={() => handleDelete(ins.id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition" title="Supprimer"><Trash2 size={16} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 border-t border-gray-200 text-xs text-gray-400 flex justify-between items-center">
          <span>{filtered.length} inscription(s) - {new Date().toLocaleString('fr-FR')}</span>
          <span className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500"></span> Formations</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-500"></span> Évaluations</span>
          </span>
        </div>
      </div>

      {/* Modals (simplifiés pour l'exemple) */}
      {showAssignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold flex items-center gap-2"><Link size={20} className="text-indigo-600" /> Assigner</h3>
              <button onClick={() => { setShowAssignModal(false); setSelectedInscriptions([]); }} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">{selectedInscriptions.length} inscription(s) sélectionnée(s)</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select value={assignType} onChange={e => setAssignType(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                  <option value="FORMATION">Formation</option>
                  <option value="EVALUATION">Évaluation</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{assignType === 'FORMATION' ? 'Formation' : 'Évaluation'}</label>
                <select value={assignEntityId} onChange={e => setAssignEntityId(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                  <option value="">-- Choisir --</option>
                  {assignType === 'FORMATION'
                    ? formations.map(f => <option key={f.id} value={f.id}>{f.titre}</option>)
                    : evaluations.map(e => <option key={e.id} value={e.id}>{e.titre}</option>)}
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button onClick={() => { setShowAssignModal(false); setSelectedInscriptions([]); }} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm">Annuler</button>
                <button onClick={handleAssign} disabled={loading || !assignEntityId} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 text-sm">{loading ? 'Assignation...' : 'Assigner'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showProgressModal && progressInscription && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold flex items-center gap-2"><BarChart3 size={20} className="text-indigo-600" /> Progression</h3>
              <button onClick={() => { setShowProgressModal(false); setProgressInscription(null); }} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Inscription ID: {progressInscription.id}</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Progression (%)</label>
                <input type="number" min="0" max="100" value={newProgression} onChange={e => setNewProgression(parseInt(e.target.value) || 0)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button onClick={() => { setShowProgressModal(false); setProgressInscription(null); }} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm">Annuler</button>
                <button onClick={handleUpdateProgression} disabled={loading} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 text-sm">{loading ? 'Mise à jour...' : 'Mettre à jour'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showProlongationModal && prolongationInscription && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold flex items-center gap-2"><CalendarClock size={20} className="text-indigo-600" /> Prolongation</h3>
              <button onClick={() => { setShowProlongationModal(false); setProlongationInscription(null); }} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Inscription ID: {prolongationInscription.id}</p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Jours supplémentaires</label>
                <input type="number" min="1" value={prolongationJours} onChange={e => setProlongationJours(parseInt(e.target.value) || 1)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Raison</label>
                <textarea value={prolongationRaison} onChange={e => setProlongationRaison(e.target.value)} rows="2" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="Motif..." />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button onClick={() => { setShowProlongationModal(false); setProlongationInscription(null); }} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm">Annuler</button>
                <button onClick={handleApproveProlongation} disabled={loading} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 text-sm">{loading ? 'Approbation...' : 'Approuver'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold flex items-center gap-2"><UserPlus size={20} className="text-indigo-600" /> Nouvelle inscription</h3>
              <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Étudiant *</label>
                <select name="etudiantId" value={formData.etudiantId} onChange={handleInputChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                  <option value="">Sélectionner</option>
                  {students.map(s => <option key={s.id} value={s.id}>{s.prenom} {s.nom} ({s.email})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select name="typeInscription" value={formData.typeInscription} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                  <option value="FORMATION">📘 Formation</option>
                  <option value="EVALUATION">📝 Évaluation</option>
                </select>
              </div>
              {formData.typeInscription === 'FORMATION' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Formation *</label>
                  <select name="formationId" value={formData.formationId} onChange={handleInputChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                    <option value="">Sélectionner</option>
                    {formations.map(f => <option key={f.id} value={f.id}>{f.titre}</option>)}
                  </select>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Évaluation *</label>
                  <select name="evaluationId" value={formData.evaluationId} onChange={handleInputChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                    <option value="">Sélectionner</option>
                    {evaluations.map(e => <option key={e.id} value={e.id}>{e.titre}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date d'inscription</label>
                <input
                  type="date"
                  name="dateInscription"
                  value={formData.dateInscription}
                  onChange={handleDateChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${dateError ? 'border-red-500' : 'border-gray-300'}`}
                />
                {/* Affichage des limites et erreurs */}
                {(dateLimits.debut || dateLimits.fin) && (
                  <div className="text-xs text-gray-500 mt-1">
                    {dateLimits.debut && <span>À partir du {dateLimits.debut} </span>}
                    {dateLimits.fin && <span>Jusqu'au {dateLimits.fin}</span>}
                  </div>
                )}
                {dateError && <div className="text-xs text-red-600 mt-1">{dateError}</div>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statut initial</label>
                <select name="statut" value={formData.statut} onChange={handleInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                  <option value="EN_ATTENTE">⏳ En attente</option>
                  <option value="VALIDEE">✅ Validée</option>
                  <option value="INSCRIT">📝 Inscrit</option>
                  <option value="EN_COURS">▶️ En cours</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => { setShowCreateModal(false); resetForm(); }} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm">Annuler</button>
                <button type="submit" disabled={loading || !!dateError} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 text-sm">
                  {loading ? 'Inscription...' : 'Inscrire'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold flex items-center gap-2"><Edit size={20} className="text-indigo-600" /> Modifier</h3>
              <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>
            <form onSubmit={handleEdit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Étudiant *</label>
                <select name="etudiantId" value={editData.etudiantId} onChange={handleEditInputChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                  <option value="">Sélectionner</option>
                  {students.map(s => <option key={s.id} value={s.id}>{s.prenom} {s.nom} ({s.email})</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Formation *</label>
                <select name="formationId" value={editData.formationId} onChange={handleEditInputChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                  <option value="">Sélectionner</option>
                  {formations.map(f => <option key={f.id} value={f.id}>{f.titre}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  name="dateInscription"
                  value={editData.dateInscription}
                  onChange={handleEditDateChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${editDateError ? 'border-red-500' : 'border-gray-300'}`}
                />
                {(editDateLimits.debut || editDateLimits.fin) && (
                  <div className="text-xs text-gray-500 mt-1">
                    {editDateLimits.debut && <span>À partir du {editDateLimits.debut} </span>}
                    {editDateLimits.fin && <span>Jusqu'au {editDateLimits.fin}</span>}
                  </div>
                )}
                {editDateError && <div className="text-xs text-red-600 mt-1">{editDateError}</div>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                <select name="statut" value={editData.statut} onChange={handleEditInputChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500">
                  <option value="EN_ATTENTE">⏳ En attente</option>
                  <option value="VALIDEE">✅ Validée</option>
                  <option value="INSCRIT">📝 Inscrit</option>
                  <option value="EN_COURS">▶️ En cours</option>
                  <option value="TERMINE">🏁 Terminé</option>
                  <option value="REJETEE">❌ Rejetée</option>
                  <option value="ANNULEE">🚫 Annulée</option>
                  <option value="ABANDONNEE">⛔ Abandonnée</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setShowEditModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm">Annuler</button>
                <button type="submit" disabled={editingLoading || !!editDateError} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 text-sm">
                  {editingLoading ? 'Mise à jour...' : 'Mettre à jour'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}